/**
 * Type-palette sidebar: lists all active ArchiMate element types from the
 * registry, grouped by layer. Each item is a drag-source whose `dataTransfer`
 * carries the type id; the canvas's drop handler creates a concept on release.
 */
import type { PaletteEntry, PaletteGroup } from "@ae/domain";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { getRegistry, resolveBilingual } from "../../registry";
import { useUi } from "../../state/ui";
import { useWorkspaceData } from "../../state/workspace";
import { getElementIcon, getStereotypeIcon } from "../canvas/elementIcons";
import { colorForConcept, colorForType } from "../layerColors";
import { PanelCollapseIcon } from "../PanelCollapseIcon";
import { panelSwatch } from "../panelSwatch";
import { usePanelWidth } from "../usePanelWidth";
import { setDraggedTypeName } from "./dragTypeName";
import { LAYER_LABELS, LAYER_ORDER, type PaletteRow, usePaletteData } from "./usePaletteData";

const COLLAPSED_GROUPS_KEY = "ae.palette.collapsed";

/**
 * State-hook voor de inklap-status van palette-groepen. Sleutels zijn
 * de section-keys die TypePalette ook als React-keys gebruikt:
 * `layer:business`, `group:c4`, etc. Persistent via localStorage zodat
 * na refresh de gebruiker z'n eigen inrichting terugziet.
 */
function useCollapsedGroups(): {
  isCollapsed: (key: string) => boolean;
  toggle: (key: string) => void;
  collapseAll: (keys: string[]) => void;
  expandAll: () => void;
  hasAnyCollapsed: boolean;
} {
  const [collapsed, setCollapsed] = useState<Set<string>>(() => {
    if (typeof window === "undefined") return new Set();
    try {
      const raw = window.localStorage.getItem(COLLAPSED_GROUPS_KEY);
      if (!raw) return new Set();
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed)
        ? new Set(parsed.filter((v): v is string => typeof v === "string"))
        : new Set();
    } catch {
      return new Set();
    }
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(COLLAPSED_GROUPS_KEY, JSON.stringify([...collapsed]));
    } catch {
      // storage unavailable — silently ignore
    }
  }, [collapsed]);

  const isCollapsed = useCallback((key: string) => collapsed.has(key), [collapsed]);
  const toggle = useCallback((key: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);
  const collapseAll = useCallback((keys: string[]) => {
    setCollapsed(new Set(keys));
  }, []);
  const expandAll = useCallback(() => {
    setCollapsed(new Set());
  }, []);

  return { isCollapsed, toggle, collapseAll, expandAll, hasAnyCollapsed: collapsed.size > 0 };
}

interface TypePaletteProps {
  /**
   * M6.5-W3b — als `true`, toon alle palette-groepen ongefilterd, ook
   * groepen met een `applies_to_view_kinds`-restrictie die normaal
   * verborgen worden wanneer er geen passend diagram actief is. Gebruikt
   * in de Base-WP-layout: daar staat geen normaal canvas, dus de
   * gebruiker (admin) moet C4/Sharp/BMC-types alsnog kunnen pakken
   * om in folders te droppen.
   */
  unconstrained?: boolean;
}

export function TypePalette({ unconstrained = false }: TypePaletteProps = {}) {
  const { t, i18n } = useTranslation();
  // M6.5-W3b — filter uit Foldertree-paneel. Alleen actief wanneer
  // `unconstrained` (= Base-WP-modus). Bij gezette filter tonen we
  // alleen palette-groups van die folder-class — én verbergen we
  // archimate-laag-groepen wanneer de filter "sharp"/"bmc"/"c4" is.
  const baseFolderClassFilter = useUi((s) => s.baseFolderClassFilter);
  const activeFilter = unconstrained ? baseFolderClassFilter : null;
  const lang = i18n.language;
  const open = useUi((s) => s.typePaletteOpen);
  const setOpen = useUi((s) => s.setTypePaletteOpen);
  const pinned = useUi((s) => s.typePalettePinned);
  const setPinned = useUi((s) => s.setTypePalettePinned);
  const { width: paletteWidth, ResizeHandle } = usePanelWidth(
    "ae.panelWidth.typePalette",
    224,
    "right",
  );
  // Subscribe to the actual view-kind id of the active diagram. When the
  // user changes the diagram type via the Diagrammen-paneel dropdown the
  // activeDiagramId stays the same but the view-kind shifts, so this
  // selector is what we really need to refresh on.
  const activeViewKind = useWorkspaceData((s) => {
    if (!s.activeDiagramId) return null;
    return s.store.getDiagram(s.activeDiagramId)?.viewKind ?? null;
  });

  const { grouped, paletteGroups } = usePaletteData({
    activeViewKind,
    unconstrained,
    activeFilter,
  });

  const collapsedGroups = useCollapsedGroups();
  // Alle section-keys die we tonen — nodig voor "alles inklappen".
  const allSectionKeys = useMemo(() => {
    const keys: string[] = [];
    for (const { layer } of grouped) keys.push(`layer:${layer}`);
    for (const g of paletteGroups) keys.push(`group:${g.id}`);
    return keys;
  }, [grouped, paletteGroups]);

  if (!open) return null;

  return (
    <aside
      data-scope="type-palette"
      className="ledger relative flex h-full shrink-0 flex-col overflow-y-auto border-(--ld-control) border-r bg-(--ld-raised)"
      style={{ width: paletteWidth }}
    >
      <header className="flex items-center justify-between border-(--ld-border) border-b bg-(--ld-surface) px-3 py-2">
        <h2 className="font-semibold text-(--ld-text) text-sm">{t("palette.title")}</h2>
        <div className="flex items-center gap-1">
          <button
            type="button"
            className="flex h-7 w-7 items-center justify-center rounded text-(--ld-text) text-base leading-none hover:bg-(--ld-active)"
            onClick={() =>
              collapsedGroups.hasAnyCollapsed
                ? collapsedGroups.expandAll()
                : collapsedGroups.collapseAll(allSectionKeys)
            }
            title={
              collapsedGroups.hasAnyCollapsed ? t("palette.expandAll") : t("palette.collapseAll")
            }
            aria-label={
              collapsedGroups.hasAnyCollapsed ? t("palette.expandAll") : t("palette.collapseAll")
            }
          >
            {collapsedGroups.hasAnyCollapsed ? "⇲" : "⇱"}
          </button>
          <button
            type="button"
            className={`rounded px-1 text-sm leading-none ${
              pinned
                ? "text-(--ld-accent) hover:text-(--ld-accent)"
                : "text-(--ld-muted) hover:text-(--ld-text)"
            }`}
            onClick={() => setPinned(!pinned)}
            title={
              pinned ? t("diagramsPanel.pin.pinnedTitle") : t("diagramsPanel.pin.unpinnedTitle")
            }
            aria-label={pinned ? t("diagramsPanel.pin.unpinAria") : t("diagramsPanel.pin.pinAria")}
            aria-pressed={pinned}
          >
            {pinned ? "📌" : "📍"}
          </button>
          <button
            type="button"
            className="flex h-7 w-7 items-center justify-center rounded text-(--ld-text) text-base leading-none hover:bg-(--ld-active)"
            onClick={() => setOpen(false)}
            title={t("settings.common.close")}
          >
            <PanelCollapseIcon />
          </button>
        </div>
      </header>
      <div className="flex flex-col gap-3 p-3">
        {(() => {
          // Interleave layer-groups en palette-groups: een palette-group
          // met `after_layer: "X"` rendert direct na laag X. Groups zonder
          // (of met onbekende) after_layer komen aan het einde, in
          // registratievolgorde. Edge-case: een palette-group met
          // after_layer naar een laag die NIET visible is op de huidige
          // view-kind (bv. DATA MODELING met after_layer="strategy" op
          // een sharp.detailed-concept view zonder Strategy-types) zou
          // anders verdwijnen — die vangen we hieronder op door 'm
          // alsnog naar trailingGroups te verschuiven.
          const visibleLayers = new Set(grouped.map((g) => g.layer));
          const groupsByAfterLayer = new Map<string, typeof paletteGroups>();
          const trailingGroups: typeof paletteGroups = [];
          const knownLayers = new Set(LAYER_ORDER);
          for (const g of paletteGroups) {
            if (
              g.after_layer &&
              knownLayers.has(g.after_layer) &&
              visibleLayers.has(g.after_layer)
            ) {
              const arr = groupsByAfterLayer.get(g.after_layer) ?? [];
              arr.push(g);
              groupsByAfterLayer.set(g.after_layer, arr);
            } else {
              trailingGroups.push(g);
            }
          }
          const blocks: React.ReactNode[] = [];
          for (const { layer, types } of grouped) {
            const sectionKey = `layer:${layer}`;
            blocks.push(
              <CollapsibleSection
                key={sectionKey}
                title={LAYER_LABELS[layer] ? t(LAYER_LABELS[layer]) : layer}
                collapsed={collapsedGroups.isCollapsed(sectionKey)}
                onToggle={() => collapsedGroups.toggle(sectionKey)}
              >
                {types.map((row) => (
                  <PaletteItem key={row.elementType.id} row={row} lang={lang} />
                ))}
              </CollapsibleSection>,
            );
            const after = groupsByAfterLayer.get(layer);
            if (after) {
              for (const g of after) {
                const gk = `group:${g.id}`;
                blocks.push(
                  <PaletteVirtualGroup
                    key={gk}
                    group={g}
                    lang={lang}
                    collapsed={collapsedGroups.isCollapsed(gk)}
                    onToggle={() => collapsedGroups.toggle(gk)}
                  />,
                );
              }
            }
          }
          for (const g of trailingGroups) {
            const gk = `group:${g.id}`;
            blocks.push(
              <PaletteVirtualGroup
                key={gk}
                group={g}
                lang={lang}
                collapsed={collapsedGroups.isCollapsed(gk)}
                onToggle={() => collapsedGroups.toggle(gk)}
              />,
            );
          }
          return blocks;
        })()}
      </div>
      <ResizeHandle />
    </aside>
  );
}

/**
 * Sectie met klikbare header (caret-toggle) en inklapbare body.
 * Gebruikt door zowel layer-groepen als virtual palette-groepen via
 * dezelfde primitive zodat het toggle-gedrag overal hetzelfde is.
 */
function CollapsibleSection({
  title,
  collapsed,
  onToggle,
  children,
}: {
  title: string;
  collapsed: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <section>
      <button
        type="button"
        onClick={onToggle}
        className="mb-1 flex w-full items-center gap-1 font-medium text-(--ld-muted) text-xs hover:text-(--ld-text)"
        aria-expanded={!collapsed}
      >
        <span aria-hidden className="inline-block w-3 text-center">
          {collapsed ? "▸" : "▾"}
        </span>
        <span className="truncate text-left">{title}</span>
      </button>
      {!collapsed && <ul className="flex flex-col gap-1">{children}</ul>}
    </section>
  );
}

function PaletteItem({ row, lang }: { row: PaletteRow; lang: string }) {
  const type = row.elementType;
  const colors = colorForType(type.id, type.layer);
  const swatch = panelSwatch(colors);
  const label = resolveBilingual(type.label, lang);
  const icon = getElementIcon(type.id);
  return (
    <li
      // biome-ignore lint/a11y/noNoninteractiveTabindex: palette-item moet keyboard-focusable zijn (W11 grid-cursor / Bucket-1 a11y); de li is draggable + handelt Enter-keydown af in een interactive pattern
      tabIndex={0}
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData("application/ae-type-id", row.dragTypeId ?? type.id);
        setDraggedTypeName(e.dataTransfer, type.label, label);
        if (row.defaultStereotypes && row.defaultStereotypes.length > 0) {
          // Zelfde MIME als de pack-palette-groepen (C4 / DATA MODELING /
          // Risk) gebruiken — de drop-handler kent 'm al.
          e.dataTransfer.setData(
            "application/ae-default-stereotypes",
            JSON.stringify(row.defaultStereotypes),
          );
        }
        // M6.5-W3b — ArchiMate-laag-groepen vallen onder folder-class
        // "archimate". FoldertreePanel-drop-target leest dit en
        // valideert tegen folder.typeGroup.
        e.dataTransfer.setData("application/ae-folder-class", "archimate");
        e.dataTransfer.effectAllowed = "copy";
      }}
      className={`flex cursor-grab select-none items-center gap-2 rounded border px-2 py-1 text-xs hover:shadow focus:outline focus:outline-2 focus:outline-(--ld-accent) ${swatch.className}`}
      style={swatch.style}
      title={type.id}
    >
      {icon ? (
        <span aria-hidden className="block h-3.5 w-3.5 shrink-0 opacity-80">
          {icon}
        </span>
      ) : (
        <span
          aria-hidden
          className="block h-2 w-2 rounded-full"
          style={{ backgroundColor: colors.border }}
        />
      )}
      <span className="truncate">{label}</span>
    </li>
  );
}

/**
 * Renders a virtual palette group (e.g. C4) — entries map to underlying
 * ArchiMate/extension types but with a curated label and optional
 * default-attributes payload. Drag-source attaches both via additional
 * `application/ae-default-attributes` MIME so the canvas drop-handler
 * can apply them at create-time.
 */
function PaletteVirtualGroup({
  group,
  lang,
  collapsed,
  onToggle,
}: {
  group: PaletteGroup;
  lang: string;
  collapsed: boolean;
  onToggle: () => void;
}) {
  return (
    <CollapsibleSection
      title={resolveBilingual(group.label, lang)}
      collapsed={collapsed}
      onToggle={onToggle}
    >
      {group.entries.map((entry, idx) => (
        <PaletteVirtualItem
          // Same type_id can appear several times in the same group with
          // different defaults (System / Container / Component all use
          // `archimate.ApplicationComponent`), so the index discriminator
          // is needed to keep React keys stable.
          // biome-ignore lint/suspicious/noArrayIndexKey: zelfde type_id komt meermaals voor in een groep (System/Container/Component = ApplicationComponent), dus de index-discriminator is nodig
          key={`${entry.type_id}-${idx}`}
          entry={entry}
          lang={lang}
          folderClass={group.folder_class ?? "archimate"}
        />
      ))}
    </CollapsibleSection>
  );
}

function PaletteVirtualItem({
  entry,
  lang,
  folderClass,
}: {
  entry: PaletteEntry;
  lang: string;
  folderClass: string;
}) {
  const { t } = useTranslation();
  const reg = getRegistry();
  const isRelation = entry.kind === "relation";
  const target = isRelation ? null : reg.getElementType(entry.type_id);
  const relationType = isRelation ? reg.getRelationType(entry.type_id) : null;
  const layer = target?.layer ?? "composite";
  // Palette-tegel-kleur: voor entries met default_stereotypes (bv. de
  // Risk-and-Security-groep, Sharp Data Modelling, BMC) gebruiken we de
  // stereotype-aware resolver zodat de tegel de echte stereotype-token
  // toont in plaats van de base-type-laag-kleur. Zonder stereotype
  // valt 'ie automatisch terug op het base-type-token / layer-default.
  const colors = isRelation
    ? { bg: "#f8fafc", border: "#94a3b8", text: "#334155" }
    : (entry.default_stereotypes?.length ?? 0) > 0
      ? colorForConcept({
          typeId: entry.type_id,
          layer,
          stereotypes: entry.default_stereotypes ?? [],
        })
      : colorForType(entry.type_id, layer);
  const labelText = entry.label_override ?? target?.label ?? relationType?.label;
  const label = labelText ? resolveBilingual(labelText, lang) : entry.type_id;
  // Stereotype-icoon (bv. «Risk» op Assessment-entry in de Risk-groep)
  // krijgt voorrang boven het base-type-icoon zodat de palette-tegel de
  // bedoelde decoratie toont.
  const icon = isRelation
    ? null
    : (getStereotypeIcon(entry.default_stereotypes ?? []) ?? getElementIcon(entry.type_id));
  const description = entry.description ? resolveBilingual(entry.description, lang) : undefined;
  // Relatie-entries zijn niet sleepbaar op leeg canvas — een lijn heeft
  // bron + doel nodig. We laten ze toch zien als referentie zodat de
  // gebruiker weet dat ze bestaan; kleine info-tooltip onder de hover
  // legt uit hoe je ze gebruikt.
  return (
    <li
      // biome-ignore lint/a11y/noNoninteractiveTabindex: palette-item moet keyboard-focusable zijn (W11 grid-cursor / Bucket-1 a11y); de li is draggable en handelt Enter-keydown af in een interactive pattern
      tabIndex={0}
      draggable={!isRelation}
      onDragStart={
        isRelation
          ? undefined
          : (e) => {
              e.dataTransfer.setData("application/ae-type-id", entry.type_id);
              setDraggedTypeName(e.dataTransfer, labelText ?? {}, label);
              // M6.5-W3b — folder-class uit parent palette-group
              // (c4/sharp/bmc) zodat FoldertreePanel kan valideren of
              // de drop in een passende folder landt.
              e.dataTransfer.setData("application/ae-folder-class", folderClass);
              if (entry.default_attributes && Object.keys(entry.default_attributes).length > 0) {
                e.dataTransfer.setData(
                  "application/ae-default-attributes",
                  JSON.stringify(entry.default_attributes),
                );
              }
              if (entry.default_stereotypes && entry.default_stereotypes.length > 0) {
                e.dataTransfer.setData(
                  "application/ae-default-stereotypes",
                  JSON.stringify(entry.default_stereotypes),
                );
              }
              e.dataTransfer.effectAllowed = "copy";
            }
      }
      className={`flex select-none items-center gap-2 rounded border px-2 py-1 text-xs hover:shadow focus:outline focus:outline-2 focus:outline-(--ld-accent) ${
        isRelation ? "cursor-default" : "cursor-grab"
      } ${panelSwatch(colors).className}`}
      style={panelSwatch(colors).style}
      title={
        isRelation
          ? `${label}\n${description ?? ""}\n\n${t("palette.relationHint")}\n(${entry.type_id})`
          : description
            ? `${label}\n${description}\n(${entry.type_id})`
            : `${label}\n(${entry.type_id})`
      }
    >
      {icon ? (
        <span aria-hidden className="block h-3.5 w-3.5 shrink-0 opacity-80">
          {icon}
        </span>
      ) : isRelation ? (
        <svg aria-hidden width={14} height={10} className="shrink-0" viewBox="0 0 14 10">
          <line
            x1="1"
            y1="5"
            x2={entry.type_id === "drawing.ArrowLine" ? 11 : 13}
            y2="5"
            stroke={colors.border}
            strokeWidth={1.4}
          />
          {entry.type_id === "drawing.ArrowLine" && (
            <polygon points="11,2 13,5 11,8" fill={colors.border} />
          )}
        </svg>
      ) : (
        <span
          aria-hidden
          className="block h-2 w-2 rounded-full"
          style={{ backgroundColor: colors.border }}
        />
      )}
      <span className="truncate">{label}</span>
    </li>
  );
}
